import React, { useState } from "react";
import { discover, discoverSave } from "../lib/api";
import { Button } from "../components/ui/button";
import { DiscoverForm } from "../components/discover/DiscoverForm";
import { SuggestionCard } from "../components/discover/SuggestionCard";
import { Plus, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const INITIAL_FORM = { citta: "", settore: "all", count: 8 };
const withIds = (list) => list.map((it, i) => ({ ...it, _id: `${i}-${it.azienda}-${it.citta}` }));

export default function Discover({ onSaved }) {
  const [form, setForm] = useState(INITIAL_FORM);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState({});

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const run = async () => {
    if (!form.citta.trim()) { toast.error("Inserisci una città"); return; }
    setLoading(true); setItems([]); setSelected({});
    try {
      const res = await discover({ citta: form.citta, settore: form.settore === "all" ? "" : form.settore, count: Number(form.count) });
      const list = withIds(res.suggestions || []);
      setItems(list);
      setSelected(Object.fromEntries(list.map((it) => [it._id, !it.duplicate])));
      const dup = res.duplicates || 0;
      toast.success(`${list.length} suggerimenti${dup ? ` · ${dup} possibili doppioni` : ""}`);
    } catch {
      toast.error("Generazione fallita, riprova");
    } finally { setLoading(false); }
  };

  const save = async () => {
    const chosen = items.filter((it) => selected[it._id]).map(({ _id, ...rest }) => rest);
    if (chosen.length === 0) { toast.error("Seleziona almeno un prospect"); return; }
    setSaving(true);
    try {
      const res = await discoverSave(chosen);
      const skipMsg = res.skipped ? ` · ${res.skipped} doppioni saltati` : "";
      toast.success(`${res.inserted} prospect aggiunti${skipMsg}`);
      setItems([]); setSelected({});
      onSaved && onSaved();
    } catch {
      toast.error("Salvataggio fallito");
    } finally { setSaving(false); }
  };

  const selCount = Object.values(selected).filter(Boolean).length;

  return (
    <div className="fade-in space-y-6 p-6 md:p-8">
      <div>
        <div className="label-caps text-primary">01 · Find — Lead Generator</div>
        <h1 className="mt-1 font-heading text-4xl font-bold tracking-tight">Scopri Aziende con AI</h1>
        <p className="mt-1 text-sm text-muted-foreground">Genera una lista di prospect da cercare per città e settore. Parti già con una lista pronta.</p>
      </div>

      <DiscoverForm form={form} onChange={setField} loading={loading} onRun={run} />

      {items.length > 0 && (
        <>
          <div className="flex items-center gap-2 rounded-lg border border-yellow-500/30 bg-yellow-500/5 px-4 py-2.5 text-xs text-yellow-500/90">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            Suggerimenti generati dall'AI — da verificare prima del contatto (nomi e dati indicativi).
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">{selCount} selezionati di {items.length}</span>
            <Button onClick={save} disabled={saving} data-testid="discover-save-btn">
              <Plus className="mr-2 h-4 w-4" /> {saving ? "Salvataggio..." : `Aggiungi al database (${selCount})`}
            </Button>
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {items.map((it, i) => (
              <SuggestionCard key={it._id} it={it} index={i} checked={!!selected[it._id]}
                onToggle={(v) => setSelected((s) => ({ ...s, [it._id]: v }))} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
